
import {defineComponent, type PropType} from "vue";
import router from "@/router";

type PropagationTreeNode = {
  id: number,
  name: string,
  isCombinedRecipe: boolean,
  children: PropagationTreeNode[],
}

type PropagationSelectionState = {
  isChecked: boolean,
  isIndeterminate: boolean,
}

type PropagationChangePreview = {
  allergenId: number,
  label: string,
  kind: "added" | "removed" | "existing",
}

export default defineComponent({
  name: "AllergenPropagationTree",
  props: {
    nodes: {
      type: Array as PropType<PropagationTreeNode[]>,
      required: true
    },
    selectionStateById: {
      type: null as unknown as PropType<Map<number, PropagationSelectionState>>,
      required: true,
      validator: (value: unknown) => value instanceof Map
    },
    changePreviewByTargetId: {
      type: null as unknown as PropType<Map<number, PropagationChangePreview[]>>,
      required: true,
      validator: (value: unknown) => value instanceof Map
    },
    disabledChangeIdsByTargetId: {
      type: Object as PropType<Record<number, number[]>>,
      required: true
    },
    root: {
      type: Boolean,
      default: false
    }
  },
  emits: ["toggle", "toggle-change"],
  setup(props, { emit }) {
    function isChecked(nodeId: number) {
      return props.selectionStateById.get(nodeId)?.isChecked || false;
    }

    function isIndeterminate(nodeId: number) {
      return props.selectionStateById.get(nodeId)?.isIndeterminate || false;
    }

    function onCheckboxChanged(nodeId: number, event: Event) {
      emit("toggle", {
        targetId: nodeId,
        checked: (event.target as HTMLInputElement).checked
      });
    }

    function onChildToggle(payload: { targetId: number, checked: boolean }) {
      emit("toggle", payload);
    }

    function getPreviewChanges(targetId: number) {
      return props.changePreviewByTargetId.get(targetId) || [];
    }

    function isChangeDisabled(targetId: number, allergenId: number) {
      return (props.disabledChangeIdsByTargetId[targetId] || []).includes(allergenId);
    }

    function onChangeToggle(targetId: number, allergenId: number) {
      emit("toggle-change", { targetId, allergenId });
    }

    function onChildChangeToggle(payload: { targetId: number, allergenId: number }) {
      emit("toggle-change", payload);
    }

    function openTargetInNewTab(node: PropagationTreeNode) {
      const route = router.resolve({
        name: node.isCombinedRecipe ? "dishesEdit" : "recipesEdit",
        params: { id: `${node.id}` }
      });

      window.open(route.href, "_blank", "noopener");
    }

    function getOpenTargetLabel(node: PropagationTreeNode) {
      return node.isCombinedRecipe
        ? "Open combined recipe"
        : "Open recipe";
    }

    function getOpenTargetTitle(node: PropagationTreeNode) {
      return node.isCombinedRecipe
        ? "Open this combined recipe in a new tab"
        : "Open this recipe in a new tab";
    }

    function getChangeIconClass(kind: PropagationChangePreview["kind"]) {
      switch (kind) {
      case "added":
        return "fa-check";
      case "removed":
        return "fa-times";
      default:
        return "fa-minus";
      }
    }

    return {
      isChecked,
      isIndeterminate,
      onCheckboxChanged,
      onChildToggle,
      getPreviewChanges,
      isChangeDisabled,
      onChangeToggle,
      onChildChangeToggle,
      openTargetInNewTab,
      getOpenTargetLabel,
      getOpenTargetTitle,
      getChangeIconClass
    };
  }
});
